import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  Users, Search, Plus, Edit, Trash2, Loader2, LogOut,
  LayoutDashboard, Monitor, Megaphone, Calendar, CheckSquare, BarChart3, CreditCard,
  Mail, Phone, Building
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { ParkWiseLogo } from "../../../components/ui/parkwise-logo";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";

const API = "/api";

const navItems = [
  { path: "/admin/advertising", icon: LayoutDashboard, label: "Dashboard" },
  { path: "/admin/advertising/advertisers", icon: Users, label: "Advertisers" },
  { path: "/admin/advertising/screens", icon: Monitor, label: "Screens & Inventory" },
  { path: "/admin/advertising/campaigns", icon: Megaphone, label: "Campaigns" },
  { path: "/admin/advertising/calendar", icon: Calendar, label: "Bookings Calendar" },
  { path: "/admin/advertising/approvals", icon: CheckSquare, label: "Artwork Approvals" },
  { path: "/admin/advertising/proof-of-play", icon: BarChart3, label: "Proof of Play" },
  { path: "/admin/advertising/billing", icon: CreditCard, label: "Billing & Invoices" },
];

const statusColors = {
  lead: "bg-slate-600",
  prospect: "bg-blue-600",
  active: "bg-green-600",
  inactive: "bg-gray-600",
  churned: "bg-red-600"
};

export default function AdvertisingAdvertisers() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [advertisers, setAdvertisers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (user) fetchAdvertisers();
  }, [user, search]);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      navigate("/admin/login");
    }
  };

  const fetchAdvertisers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      const response = await axios.get(`${API}/advertising/advertisers?${params}`, { withCredentials: true });
      setAdvertisers(response.data);
    } catch (error) {
      console.error("Failed to fetch advertisers:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {}
    navigate("/admin/login");
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 flex">
      {/* Sidebar */}
      <aside className="w-72 bg-slate-900 border-r border-slate-800 flex flex-col">
        <div className="p-6 border-b border-slate-800">
          <ParkWiseLogo size="md" />
          <p className="text-slate-500 text-xs mt-2">Advertising & Campaigns</p>
        </div>
        
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive ? "bg-blue-600/20 text-blue-400" : "text-slate-400 hover:text-white hover:bg-slate-800"
                }`}
              >
                <item.icon className="w-5 h-5" />
                <span className="text-sm font-medium">{item.label}</span>
              </button>
            );
          })}
          <div className="pt-4 border-t border-slate-800 mt-4">
            <button onClick={() => navigate("/admin/dashboard")} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-slate-500 hover:text-white hover:bg-slate-800">
              <LayoutDashboard className="w-5 h-5" />
              <span className="text-sm">← Back to Main Admin</span>
            </button>
          </div>
        </nav>

        <div className="p-4 border-t border-slate-800">
          <div className="flex items-center gap-3 mb-3 px-2">
            <ParkWiseLogo size="sm" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-2 text-slate-400 hover:text-white text-sm w-full px-2">
            <LogOut className="w-4 h-4" /><span>Sign out</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-8 overflow-auto">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl font-bold text-white">Advertisers</h1>
              <p className="text-slate-400 mt-1">Manage advertising clients and contacts</p>
            </div>
            <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-advertiser-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Advertiser
            </Button>
          </div>

          {/* Search & Filters */}
          <div className="mb-6 flex items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search advertisers..."
                className="pl-10 bg-slate-900 border-slate-700 text-white"
                data-testid="search-advertisers"
              />
            </div>
            <div className="text-sm text-slate-400">
              <strong className="text-white">{advertisers.length}</strong> advertisers
            </div>
          </div>

          {/* Advertisers Table */}
          <Card className="bg-slate-900 border-slate-800">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
              </div>
            ) : advertisers.length === 0 ? (
              <CardContent className="py-12 text-center">
                <Users className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">No advertisers found. Add your first client to get started.</p>
              </CardContent>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800">
                    <TableHead className="text-slate-400">Company</TableHead>
                    <TableHead className="text-slate-400">Contact</TableHead>
                    <TableHead className="text-slate-400">Industry</TableHead>
                    <TableHead className="text-slate-400">Status</TableHead>
                    <TableHead className="text-slate-400">Campaigns</TableHead>
                    <TableHead className="text-slate-400">Total Spend</TableHead>
                    <TableHead className="text-slate-400">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {advertisers.map(adv => (
                    <TableRow key={adv.advertiser_id} className="border-slate-800 hover:bg-slate-800/50">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-slate-700 flex items-center justify-center">
                            <Building className="w-5 h-5 text-slate-400" />
                          </div>
                          <div>
                            <p className="text-white font-medium">{adv.company_name}</p>
                            <p className="text-xs text-slate-500">{adv.advertiser_id}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="text-slate-300">{adv.contact_name}</p>
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <Mail className="w-3 h-3" /> {adv.email}
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-300">{adv.industry || "-"}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs text-white ${statusColors[adv.status] || 'bg-slate-600'}`}>
                          {adv.status}
                        </span>
                      </TableCell>
                      <TableCell className="text-slate-300">{adv.campaign_count || 0}</TableCell>
                      <TableCell className="text-emerald-400 font-medium">£{(adv.total_spend || 0).toLocaleString()}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-slate-400 hover:text-red-400">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </div>
      </main>
    </div>
  );
}
